/**
 * React hook for chat session management and SSE streaming.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { createChatSession, getChatSession, sendChatMessage } from '../../api/chat';

export function useChatSession() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  // Initialize or resume a session
  const initSession = useCallback(async (existingSessionId) => {
    try {
      setError(null);
      if (existingSessionId) {
        const data = await getChatSession(existingSessionId);
        if (data.ok) {
          setSessionId(existingSessionId);
          setMessages(data.session?.messages || []);
          return existingSessionId;
        }
      }
      const data = await createChatSession();
      if (data.ok) {
        setSessionId(data.session_id);
        setMessages([]);
        return data.session_id;
      }
    } catch (err) {
      setError(err.message);
    }
    return null;
  }, []);

  // Send a message and process the SSE stream
  const sendMessage = useCallback(async (text, mode = 'default') => {
    if (!sessionId || isStreaming) return;

    setError(null);
    setIsStreaming(true);

    // Add user message immediately
    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);

    // Create placeholder for assistant response
    const assistantMsg = { role: 'assistant', content: '', toolCalls: [], timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, assistantMsg]);

    try {
      const body = await sendChatMessage(sessionId, text, mode);
      const reader = body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));

            switch (event.type) {
              case 'text_delta':
                setMessages(prev => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, content: last.content + event.text };
                  }
                  return updated;
                });
                break;

              case 'tool_use':
                setMessages(prev => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === 'assistant') {
                    const toolCalls = [...(last.toolCalls || []), { name: event.name, input: event.input, result: null }];
                    updated[updated.length - 1] = { ...last, toolCalls };
                  }
                  return updated;
                });
                break;

              case 'tool_result':
                setMessages(prev => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === 'assistant') {
                    const toolCalls = (last.toolCalls || []).map(tc =>
                      tc.name === event.name && !tc.result ? { ...tc, result: event.result } : tc
                    );
                    updated[updated.length - 1] = { ...last, toolCalls };
                  }
                  return updated;
                });
                break;

              case 'status':
                setMessages(prev => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, status: event.message };
                  }
                  return updated;
                });
                break;

              case 'error':
                setError(event.message);
                break;

              case 'done':
                break;
            }
          } catch {
            // Skip malformed events
          }
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsStreaming(false);
    }
  }, [sessionId, isStreaming]);

  // New session
  const newSession = useCallback(async () => {
    const data = await createChatSession();
    if (data.ok) {
      setSessionId(data.session_id);
      setMessages([]);
      return data.session_id;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  return {
    sessionId,
    messages,
    isStreaming,
    error,
    initSession,
    sendMessage,
    newSession,
  };
}
